import { useState } from "react";
import { useApi } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Sparkles, ArrowUpCircle, Bug, Rocket, Tag } from "lucide-react";

const TYPE = {
  feature: { label: "New", icon: Sparkles, cls: "text-[hsl(var(--sev-success))] border-[hsl(var(--sev-success)/0.4)] bg-[hsl(var(--sev-success)/0.08)]" },
  improvement: { label: "Improved", icon: ArrowUpCircle, cls: "text-[hsl(var(--sev-info))] border-[hsl(var(--sev-info)/0.4)] bg-[hsl(var(--sev-info)/0.08)]" },
  fix: { label: "Fixed", icon: Bug, cls: "text-gold border-gold/40 bg-gold/10" },
};

export function VersionOverlay() {
  const [open, setOpen] = useState(false);
  const { data } = useApi("changelog", "/changelog");
  const current = data?.current;
  const releases = data?.releases || [];
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="os-version"
        className="inline-flex items-center gap-1.5 rounded-full border border-border bg-foreground/5 px-2.5 py-1 text-[11px] font-semibold text-foreground hover:bg-foreground/10 hover:border-foreground/30 transition-colors"
        title="What's new in this version">
        <Sparkles className="h-3 w-3 text-gold" />v{current || "…"}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="version-overlay">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Rocket className="h-4 w-4 text-navy" />What&rsquo;s new in Navitra OS</DialogTitle>
            <DialogDescription>Release notes · currently on v{current}</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 max-h-[60vh] overflow-y-auto thin-scroll pr-1">
            {releases.map((r, ri) => (
              <div key={r.version} data-testid={`release-${r.version}`} className="relative pl-4">
                <span className={`absolute left-0 top-1.5 h-2 w-2 rounded-full ${ri === 0 ? "bg-[hsl(var(--sev-success))]" : "bg-muted-foreground/40"}`} />
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display text-base">v{r.version}</span>
                  {ri === 0 && <Badge variant="outline" className="text-[10px] text-[hsl(var(--sev-success))] border-[hsl(var(--sev-success)/0.4)] bg-[hsl(var(--sev-success)/0.08)]">Latest</Badge>}
                  <span className="text-[11px] text-muted-foreground">{r.date}</span>
                </div>
                <p className="text-sm font-medium mt-0.5">{r.title}</p>
                <ul className="mt-2 space-y-1.5">
                  {r.items.map((it, i) => {
                    const t = TYPE[it.type] || TYPE.improvement;
                    const Icon = t.icon;
                    return (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Badge variant="outline" className={`text-[9px] mt-0.5 gap-1 shrink-0 ${t.cls}`}><Icon className="h-2.5 w-2.5" />{t.label}</Badge>
                        <span className="text-muted-foreground leading-snug">{it.text}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
